import { HStack, Stack, Steps } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  LuCalendar,
  LuCheck,
  LuSlidersHorizontal,
  LuTarget,
} from "react-icons/lu";

import { createOptimizationRule } from "../../api/optimization-rule";
import { useAppSelector, useLoading, useToaster } from "../../hooks";
import {
  BiddingStrategy,
  OptimizationPeriod,
  OptimizationTarget,
} from "../../types/optimization-rule";
import { Button } from "../button";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "../dialog";
import IconCircle from "../icon-circle";
import { toaster } from "../toaster";
import { BiddingStep } from "./bidding-step";
import { CustomStep } from "./custom-step";
import { NameStep } from "./name-step";
import { TargetStep } from "./target-step";
import { TimeframeStep } from "./timeframe-step";
import { OptimizationFormData, optimizationFormSchema } from "./types";

interface StepConfig {
  label: string;
  icon: React.ReactNode;
  Component: React.FC;
}

interface StepProps {
  target: OptimizationTarget;
  workspace: { slug: string };
}

const createSteps = ({ target, workspace }: StepProps): StepConfig[] => {
  const commonSteps = [
    {
      label: "Name & Description",
      icon: <LuSlidersHorizontal />,
      Component: () => <NameStep />,
    },
    {
      label: "Target",
      icon: <LuTarget />,
      Component: () => <TargetStep />,
    },
  ];

  if (target === OptimizationTarget.FOP) {
    return commonSteps;
  }

  const additionalSteps = [
    ...(target === OptimizationTarget.Custom
      ? [
          {
            label: "Custom",
            icon: <LuSlidersHorizontal />,
            Component: () => <CustomStep />,
          },
        ]
      : []),
    {
      label: "Timeframe",
      icon: <LuCalendar />,
      Component: () => <TimeframeStep />,
    },
    {
      label: "Bidding Strategy",
      icon: <LuTarget />,
      Component: () => <BiddingStep workspaceSlug={workspace.slug!} />,
    },
  ];

  return [...commonSteps, ...additionalSteps];
};

interface CreateOptimizationRuleDialogProps {
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
}

export function CreateOptimizationRuleDialog({
  isOpen,
  onOpenChange,
}: CreateOptimizationRuleDialogProps) {
  const [step, setStep] = React.useState(0);
  const [, wrapLoading] = useLoading();
  const wrapToast = useToaster();
  const workspace = useAppSelector((state) => state.workspace);
  const methods = useForm<OptimizationFormData>({
    defaultValues: {
      name: "",
      description: "",
      target: OptimizationTarget.CLTV,
      timeframe: "365",
      biddingStrategy: BiddingStrategy.Value,
      customValueCutoff: null,
      customCpaCutoff: 0.25,
      customFopWeight: 0.0,
      customCltvWeight: 1.0,
      customCltpWeight: 0.0,
    },
    resolver: zodResolver(optimizationFormSchema),
  });
  const {
    reset,
    watch,
    formState: { isSubmitting },
  } = methods;
  const target = watch("target");
  const steps = React.useMemo(
    () => createSteps({ target, workspace }),
    [target, workspace],
  );
  const findFirstStepWithErrors = React.useCallback(
    (formErrors: any) => {
      const errorSteps = [
        { step: 0, condition: formErrors.name || formErrors.description },
        { step: 1, condition: formErrors.target },
        {
          step: 2,
          condition:
            target === OptimizationTarget.Custom &&
            (formErrors.customFopWeight ||
              formErrors.customCltvWeight ||
              formErrors.customCltpWeight),
        },
        {
          step: 3,
          condition: formErrors.timeframe,
        },
        {
          step: 4,
          condition: formErrors.biddingStrategy,
        },
      ];

      return errorSteps.find(({ condition }) => condition)?.step ?? null;
    },
    [target],
  );
  const onSubmit = async (formData: OptimizationFormData) => {
    await wrapToast(async () => {
      await wrapLoading(async () => {
        await createOptimizationRule(workspace.slug!, {
          name: formData.name,
          description: formData.description,
          target: formData.target,
          period:
            formData.target === OptimizationTarget.FOP
              ? undefined
              : (Number(formData.timeframe) as OptimizationPeriod),
          "bidding-strategy":
            formData.target === OptimizationTarget.FOP
              ? undefined
              : formData.biddingStrategy,
          "custom-value-cutoff":
            formData.biddingStrategy === BiddingStrategy.Value
              ? (formData.customValueCutoff ?? null)
              : null,
          "custom-cpa-cutoff":
            formData.biddingStrategy === BiddingStrategy.LowestCPA
              ? (formData.customCpaCutoff ?? 0.25)
              : null,
          "custom-fop-weight": formData.customFopWeight,
          "custom-cltv-weight": formData.customCltvWeight,
          "custom-cltp-weight": formData.customCltpWeight,
          "max-value": formData.maxValue ?? null,
          "min-value-%ile": formData.minValuePercentile ?? null,
          "max-value-%ile": formData.maxValuePercentile ?? null,
          "avg-order-value": formData.avgOrderValue ?? null,
        });
        toaster.create({
          type: "success",
          title: "Success",
          description: "Conversion event created successfully",
        });
        onOpenChange({ open: false });
        setStep(0);
        reset();
      });
    });
  };
  const onError = React.useCallback(
    (formErrors: any) => {
      const firstErrorStep = findFirstStepWithErrors(formErrors);
      if (firstErrorStep !== null) {
        setStep(firstErrorStep);
      }
    },
    [findFirstStepWithErrors],
  );
  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent bg="bg.muted">
        <FormProvider {...methods}>
          <form
            id="optimization-form"
            onSubmit={methods.handleSubmit(onSubmit, onError)}
          >
            <Steps.Root
              count={steps.length}
              gap="0"
              onStepChange={(e) => setStep(e.step)}
              size="xs"
              step={step}
              variant="subtle"
            >
              <DialogCloseTrigger colorPalette="gray" />
              <DialogHeader bg="bg" borderTopRadius="md">
                <Stack gap="4" w="full">
                  <HStack>
                    <IconCircle Icon={LuSlidersHorizontal} />
                    <DialogTitle>Create Conversion Event</DialogTitle>
                  </HStack>
                  <Steps.List>
                    {steps.map((s, idx) => (
                      <Steps.Item key={s.label} index={idx} title={s.label}>
                        <Steps.Indicator>
                          <Steps.Status
                            incomplete={s.icon}
                            complete={<LuCheck />}
                          />
                        </Steps.Indicator>
                        <Steps.Separator />
                      </Steps.Item>
                    ))}
                  </Steps.List>
                </Stack>
              </DialogHeader>
              <DialogBody
                bg="bg"
                borderBottomRadius="md"
                borderBottomWidth="1"
                display="flex"
                minH="sm"
              >
                {steps.map((s, idx) => (
                  <Steps.Content
                    display="flex"
                    flex="1"
                    index={idx}
                    key={s.label}
                  >
                    <s.Component />
                  </Steps.Content>
                ))}
              </DialogBody>
              <DialogFooter>
                <Steps.PrevTrigger asChild>
                  <Button colorPalette="gray" variant="surface">
                    Back
                  </Button>
                </Steps.PrevTrigger>
                {step < steps.length - 1 ? (
                  <Steps.NextTrigger asChild>
                    <Button>Continue</Button>
                  </Steps.NextTrigger>
                ) : (
                  <Button
                    type="submit"
                    form="optimization-form"
                    loading={isSubmitting}
                  >
                    Create
                  </Button>
                )}
              </DialogFooter>
            </Steps.Root>
          </form>
        </FormProvider>
      </DialogContent>
    </DialogRoot>
  );
}
